/**
 * Which install story the browser in front of us actually has.
 *
 * One of the four modules of the shared install offer — this file,
 * ./install-state.ts (the policy and its storage), ./install-prompt.ts (the
 * early `beforeinstallprompt` capture) and ../hooks/use-install-offer.ts (the
 * decision, as a hook). Promoted from apps/retells/src/install/, which still
 * owns the panel that renders it; see ./install-prompt.ts for why this design,
 * and not the `usePWAInstall` hook ported from sara/app, is the one kept.
 *
 * There are only two, and the difference is the whole reason this module
 * exists:
 *
 *  - **Chromium** fires `beforeinstallprompt`, which hands the page a deferred
 *    handle to the browser's own install dialog. One button, one tap, done.
 *    Nothing here has to know what the browser's menus look like.
 *  - **iOS Safari** does not, and never has. Installing is Share → Add to Home
 *    Screen → Add, performed by hand in browser chrome the page cannot reach
 *    or point at. The only thing a web page can do is *say so* — which is why
 *    this site carries install instructions at all rather than just a button.
 *
 * Everything else (Firefox, desktop Safari) is deliberately left alone: see
 * `describeOffer` in ../hooks/use-install-offer.ts for why guessing at a menu
 * path we cannot verify is worse than staying quiet.
 *
 * The inputs are parameters rather than reads of `navigator`, so this stays
 * importable under the prerender (no `navigator` there) and testable under
 * vitest's node environment (no `navigator` there either) — the same shape as
 * the storage handle in ./install-state.ts.
 */

/** Which set of install instructions, if any, applies. */
export type InstallPlatform = "ios" | "android" | "desktop" | "other";

/**
 * Read the platform off a user-agent string.
 *
 * `maxTouchPoints` is not a nicety. Since iPadOS 13 an iPad reports itself as
 * `Macintosh; Intel Mac OS X` — the same UA a desktop Mac sends — so a string
 * match alone files every iPad under `desktop`. What that costs is silence,
 * not a wrong button: an iPad never fires `beforeinstallprompt`, so
 * `describeOffer` (../hooks/use-install-offer.ts) resolves `desktop` with no
 * native handle to `hidden`, and the one device whose install path this site actually
 * spells out is the one device never shown it. A Mac with a touchscreen does
 * not exist, so "claims to be a Mac and has touch points" is the standard, and
 * reliable, tell.
 *
 * Order matters below: iOS is checked before Android and before desktop
 * because a Chrome-on-iOS UA contains `CriOS` on top of an iPhone string, and
 * it is still WebKit underneath, so it still has no `beforeinstallprompt`.
 */
export function detectPlatform(
  userAgent: string,
  maxTouchPoints: number,
): InstallPlatform {
  const isIosDevice = /iPhone|iPad|iPod/.test(userAgent);
  const isTouchMac = userAgent.includes("Macintosh") && maxTouchPoints > 1;
  if (isIosDevice || isTouchMac) return "ios";

  if (userAgent.includes("Android")) return "android";

  // Anything left that reads as a desktop OS. Not "not mobile" — an unknown
  // UA should land in `other` and be left alone, not be assumed to be a
  // laptop.
  if (/Windows|Macintosh|CrOS|X11|Linux/.test(userAgent)) return "desktop";

  return "other";
}

/**
 * `detectPlatform` against the live browser. Returns `other` off-browser.
 *
 * `maxTouchPoints` sits *within* BROWSER_FLOOR (packages/config/vite.ts) as of
 * the ES2020 floor: at chrome80/edge80/firefox74/safari14 the warn-only
 * `compat/compat` lint no longer flags the line below, where at the previous
 * safari12 floor it reported "not supported in Safari 12, iOS Safari
 * 12.0-12.1" on every run. The line is still written to be read straight
 * through the property rather than behind an `as Navigator & { ... }` cast, so
 * that lint is the thing that speaks up the next time the floor moves — in
 * either direction.
 *
 * The `?? 0` therefore guards nothing the floor still admits, and is kept only
 * because the property exists here for one job: telling an iPad that claims to
 * be a Macintosh apart from a real Mac. That claim started with iPadOS *13*,
 * and any browser old enough to lack the property still says `iPad` in its
 * user agent and is caught by the string test before the touch count is ever
 * consulted — so the fallback costs nothing rather than mis-filing an old iPad
 * as a desktop.
 */
export function currentPlatform(): InstallPlatform {
  if (typeof navigator === "undefined") return "other";
  return detectPlatform(navigator.userAgent, navigator.maxTouchPoints ?? 0);
}

/**
 * Is this document already the installed app?
 *
 * Two checks because the two platforms answer in different places. The
 * `display-mode` media query is the standard one and matches the
 * `"display": "standalone"` an installable app's web manifest declares (retells:
 * apps/retells/public/manifest.webmanifest). `navigator.standalone` is
 * Safari's own non-standard flag, and on older iOS it is the only one that
 * answers — which is precisely the platform whose instructions are the most
 * annoying to be shown after you have already followed them.
 *
 * A false negative here costs a visitor one dismissal. A false positive means
 * the offer never appears at all, so both checks are ORed rather than ANDed.
 */
export function isRunningInstalled(): boolean {
  if (typeof window === "undefined") return false;

  try {
    if (window.matchMedia("(display-mode: standalone)").matches) return true;
  } catch {
    // `matchMedia` with an unparseable query throws in older WebKit rather
    // than returning a non-matching list.
  }

  return (
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  );
}
